import { useMemo, useState, useCallback, useEffect } from 'react'
import type { SortingState, ColumnSizingState } from '@tanstack/react-table'
import { useView } from '@/hooks/use-views'
import { useFieldDefinitions } from '@/hooks/use-field-definitions'
import { useCompaniesSearch } from '@/hooks/use-companies'
import {
  buildColumnDefs,
  getNestedValue,
  getBadgeConfig,
  sortingStateToSortDto,
  sortDtoToSortingState,
  columnIdToFieldName,
  fieldNameToColumnId,
  type ColumnDef,
  type DataType,
} from '@/components/custom/datatable/types'
import type {
  ViewEntityType,
  Filter,
  Company,
  ViewColumn,
  Sort,
} from '@/types/api'
import { useViewsStore } from '@/stores/views.store'
import { viewFiltersToFilterDtos } from '@/lib/filter-utils'
import { useUrlSync } from '@/hooks/use-url-sync'
import { useDebouncedValue } from '@/hooks/use-debounced-value'

interface UseDataTableParams {
  entityType: ViewEntityType
  viewId: string | null
  /** Enable URL sync for filters/sorts/search (default: true) */
  enableUrlSync?: boolean
  /**
   * Optional predicate to hide columns from:
   * - the rendered table
   * - the column visibility selector
   *
   * Intended for context-dependent columns (e.g., fund-scoped columns).
   */
  isColumnAvailable?: (
    fieldName: string,
    ctx: {
      entityType: ViewEntityType
      viewId: string | null
      filters: Filter[]
      sorts: Sort[]
      searchTerm: string
    }
  ) => boolean
}

interface ColumnVisibilityItem {
  id: string
  label: string
  isVisible: boolean
}

interface UseDataTableResult {
  // Data
  data: Company[]
  columns: ColumnDef<Company>[]
  // Loading states
  isLoading: boolean
  isLoadingMore: boolean
  isError: boolean
  // Pagination
  hasMore: boolean
  fetchMore: () => void
  // Sorting
  sorting: SortingState
  onSortChange: (sorting: SortingState) => void
  onSortSelect: (sortValue: string) => void
  onSortUpdate: (oldSort: string, newSortValue: SortingState) => void
  onSortReorder: (newSorting: SortingState) => void
  clearSorts: (sortId?: string) => void
  // Column order
  columnOrder: string[]
  onColumnOrderChange: (columnOrder: string[]) => void
  // Column sizing
  columnSizing: ColumnSizingState
  onColumnSizingChange: (columnId: string, width: number) => void
  // Column visibility
  columnVisibility: Record<string, boolean>
  columnVisibilityItems: ColumnVisibilityItem[]
  onColumnVisibilityChange: (columnId: string, isVisible: boolean) => void
  onBatchColumnVisibilityChange: (changes: Record<string, boolean>) => void
  // Search
  searchTerm: string
  onSearchChange: (value: string) => void
  // Filters
  filters: Filter[]
  onFilterChange: (filters: Filter[]) => void
  // Unsaved changes
  hasUnsavedChanges: boolean
  resetFiltersAndSorts: () => void
  revertAllChanges: () => void
  // Current column state (for explicit save)
  getCurrentColumns: () => {
    fieldName: string
    displayOrder: number
    isVisible: boolean
    width?: number
  }[]
  // Refetch
  refetch: () => void
}

/**
 * Hook that combines view, field definitions, and data fetching
 * for a complete datatable experience.
 *
 * State derivation pattern:
 * - Base state is derived directly from view data (source of truth)
 * - Working state (in Zustand store) tracks user modifications
 * - URL is synced with working state for shareability
 * - When viewId changes, working state is re-initialized from new view
 */
export function useDataTable({
  entityType,
  viewId,
  enableUrlSync = true,
  isColumnAvailable,
}: UseDataTableParams): UseDataTableResult {
  // Get store actions
  const initWorkingState = useViewsStore((state) => state.initWorkingState)
  const setWorkingFilters = useViewsStore((state) => state.setWorkingFilters)
  const setWorkingSorts = useViewsStore((state) => state.setWorkingSorts)
  const setWorkingSearchTerm = useViewsStore(
    (state) => state.setWorkingSearchTerm
  )
  const setHasPendingColumnChanges = useViewsStore(
    (state) => state.setHasPendingColumnChanges
  )
  const resetWorkingState = useViewsStore((state) => state.resetWorkingState)
  const checkHasUnsavedChanges = useViewsStore(
    (state) => state.hasUnsavedChanges
  )

  // Subscribe to working state for this view (using selector for reactivity)
  const workingStateByView = useViewsStore((state) => state.workingStateByView)
  const workingState = viewId ? workingStateByView[viewId] : undefined

  // Memoize working state values to prevent useMemo dependency changes
  const workingFilters = useMemo(
    () => workingState?.filters ?? [],
    [workingState?.filters]
  )
  const workingSorts = useMemo(
    () => workingState?.sorts ?? [],
    [workingState?.sorts]
  )
  const searchTerm = workingState?.searchTerm ?? ''
  const debouncedSearchTerm = useDebouncedValue(searchTerm)

  // Local overrides for column order, visibility, and sizing
  // Store with viewId to auto-clear when view changes
  const [columnOrderOverride, setColumnOrderOverride] = useState<{
    viewId: string | null
    value: string[] | null
  }>({ viewId, value: null })
  const [columnVisibilityOverride, setColumnVisibilityOverride] = useState<{
    viewId: string | null
    value: Record<string, boolean> | null
  }>({ viewId, value: null })
  const [columnSizingOverride, setColumnSizingOverride] = useState<{
    viewId: string | null
    value: ColumnSizingState | null
  }>({ viewId, value: null })

  // Get current override value (null if viewId changed)
  const currentColumnOrderOverride =
    columnOrderOverride.viewId === viewId ? columnOrderOverride.value : null
  const currentColumnVisibilityOverride =
    columnVisibilityOverride.viewId === viewId
      ? columnVisibilityOverride.value
      : null
  const currentColumnSizingOverride =
    columnSizingOverride.viewId === viewId ? columnSizingOverride.value : null

  // Fetch view definition (includes columns, filters, sorts)
  const {
    data: view,
    isLoading: isLoadingView,
    isError: isViewError,
  } = useView(viewId)

  // Fetch field definitions for this entity type
  const { fieldDefinitions: rawFieldDefinitions, isLoading: isLoadingFields } =
    useFieldDefinitions(entityType)

  // Extract view properties for stable dependencies
  const viewSorts = view?.sorts
  const viewFilters = view?.filters
  const rawViewColumns = view?.columns

  const fieldDefinitions = useMemo(() => {
    // Interim hide of industries field across the datatable until Greybull
    // refreshes their sector definitions (May 7 follow-up).
    const withoutHidden = rawFieldDefinitions.filter(
      (field) => field.fieldName !== 'industries'
    )
    if (!isColumnAvailable) return withoutHidden
    const ctx = {
      entityType,
      viewId,
      filters: workingFilters,
      sorts: workingSorts,
      searchTerm,
    }
    return withoutHidden.filter((field) =>
      isColumnAvailable(field.fieldName, ctx)
    )
  }, [
    rawFieldDefinitions,
    isColumnAvailable,
    entityType,
    viewId,
    workingFilters,
    workingSorts,
    searchTerm,
  ])

  const viewColumns = useMemo(() => {
    if (!rawViewColumns) return rawViewColumns
    if (!isColumnAvailable) return rawViewColumns
    const allowed = new Set(fieldDefinitions.map((f) => f.fieldName))
    return rawViewColumns.filter((col) => allowed.has(col.fieldName))
  }, [rawViewColumns, isColumnAvailable, fieldDefinitions])

  // Initialize working state when view loads
  useEffect(() => {
    if (!viewId || !view) return

    // Convert view filters/sorts to API format for initialization
    const baseFilters = viewFilters ? viewFiltersToFilterDtos(viewFilters) : []
    const baseSorts: Sort[] = viewSorts
      ? viewSorts.map((s) => ({
          field: s.fieldName,
          direction: s.direction.toLowerCase() as 'asc' | 'desc',
          order: s.sortOrder,
        }))
      : []

    initWorkingState(viewId, baseFilters, baseSorts)
  }, [viewId, view, viewFilters, viewSorts, initWorkingState])

  // Derive sorting from working state (converted to TanStack format)
  const sorting = useMemo(() => {
    if (workingSorts.length > 0) {
      return sortDtoToSortingState(workingSorts)
    }
    return []
  }, [workingSorts])

  // URL sync hook - syncs working state to URL
  const handleUrlStateChange = useCallback(
    (state: { filters: Filter[]; sorts: Sort[]; searchTerm: string }) => {
      if (!viewId) return
      setWorkingFilters(viewId, state.filters)
      setWorkingSorts(viewId, state.sorts)
      setWorkingSearchTerm(viewId, state.searchTerm)
    },
    [viewId, setWorkingFilters, setWorkingSorts, setWorkingSearchTerm]
  )

  useUrlSync({
    filters: workingFilters,
    sorts: workingSorts,
    searchTerm,
    onUrlStateChange: handleUrlStateChange,
    enabled: enableUrlSync && !!viewId,
  })

  // Combine search term with filters for API request
  // Exclude filters with empty values (user selected field but no values yet)
  const effectiveFilters = useMemo(() => {
    const filters = workingFilters.filter((f) => {
      const value = f.value
      // Filter out empty arrays (user selected field but no values)
      if (Array.isArray(value) && value.length === 0) return false
      // Filter out null/undefined values
      if (value === null || value === undefined) return false
      return true
    })

    // Add search filter if search term is provided
    if (debouncedSearchTerm.trim()) {
      filters.push({
        field: 'displayName',
        op: 'contains',
        value: debouncedSearchTerm.trim(),
        logicalOp: 'AND',
      })
    }

    return filters
  }, [workingFilters, debouncedSearchTerm])

  // Fetch companies data
  // The loaded view has already hydrated the complete working filters and sorts.
  // Sending viewId would make every table request load and merge the same view again.
  // Defer the fetch until the view AND the working state (URL hydration +
  // view-derived defaults) are both ready. Without this gate the query
  // fires once with empty filters/sorts and again after hydration,
  // doubling the network cost on every initial mount.
  const isSearchReady = !!view && !!workingState
  const {
    data,
    isLoading: isLoadingData,
    isLoadingMore,
    isError: isDataError,
    hasMore,
    fetchMore,
    refetch,
  } = useCompaniesSearch({
    filters: effectiveFilters,
    sorts: workingSorts,
    enabled: isSearchReady,
  })

  // Build column definitions from view columns and field definitions
  const allColumns = useMemo(() => {
    if (!viewColumns) return []
    return buildColumnDefs<Company>(viewColumns, fieldDefinitions)
  }, [viewColumns, fieldDefinitions])

  // Build a map of field definitions for quick lookup
  const fieldDefinitionMap = useMemo(() => {
    return new Map(fieldDefinitions.map((f) => [f.fieldName, f]))
  }, [fieldDefinitions])

  // Derive column order from view (source of truth) or use local override
  const columnOrder = useMemo(() => {
    if (currentColumnOrderOverride !== null) {
      return currentColumnOrderOverride
    }

    if (
      !viewColumns ||
      viewColumns.length === 0 ||
      fieldDefinitions.length === 0
    ) {
      return []
    }

    // Start with view columns sorted by displayOrder, converted to column IDs
    // Deduplicate by using a Set to track seen field names
    const seenFieldNames = new Set<string>()
    const viewColumnOrder: string[] = []

    const sortedViewColumns = [...viewColumns].sort(
      (a, b) => a.displayOrder - b.displayOrder
    )

    for (const col of sortedViewColumns) {
      if (!seenFieldNames.has(col.fieldName)) {
        seenFieldNames.add(col.fieldName)
        viewColumnOrder.push(fieldNameToColumnId(col.fieldName))
      }
    }

    // Add field definitions that aren't in the view (using column IDs)
    const remainingFields = fieldDefinitions
      .filter((field) => !seenFieldNames.has(field.fieldName))
      .map((field) => fieldNameToColumnId(field.fieldName))

    return [...viewColumnOrder, ...remainingFields]
  }, [currentColumnOrderOverride, viewColumns, fieldDefinitions])

  // Derive column visibility from view (source of truth) or use local override
  const columnVisibility = useMemo(() => {
    if (currentColumnVisibilityOverride !== null) {
      return currentColumnVisibilityOverride
    }

    if (
      !viewColumns ||
      viewColumns.length === 0 ||
      fieldDefinitions.length === 0
    ) {
      return {}
    }

    const visibility: Record<string, boolean> = {}

    // Create a map of view columns for quick lookup
    const viewColumnMap = new Map(
      viewColumns.map((col) => [col.fieldName, col])
    )

    // Set visibility for all field definitions using column IDs as keys
    // Columns in view use their isVisible state, others default to hidden
    fieldDefinitions.forEach((field) => {
      const viewColumn = viewColumnMap.get(field.fieldName)
      const columnId = fieldNameToColumnId(field.fieldName)
      visibility[columnId] = viewColumn?.isVisible ?? false
    })

    return visibility
  }, [currentColumnVisibilityOverride, viewColumns, fieldDefinitions])

  // Derive column sizing from view (source of truth) or use local override
  const columnSizing = useMemo((): ColumnSizingState => {
    if (currentColumnSizingOverride !== null) {
      return currentColumnSizingOverride
    }

    if (!viewColumns || viewColumns.length === 0) {
      return {}
    }

    const sizing: ColumnSizingState = {}
    viewColumns.forEach((col) => {
      if (col.width) {
        const columnId = fieldNameToColumnId(col.fieldName)
        sizing[columnId] = col.width
      }
    })

    return sizing
  }, [currentColumnSizingOverride, viewColumns])

  // Track which columns are actually in the view (not just derived from field definitions)
  // This is used to prevent saving ALL field definitions when only view columns should be saved
  const viewColumnFieldNames = useMemo(() => {
    return new Set((viewColumns ?? []).map((col) => col.fieldName))
  }, [viewColumns])

  // Filter and build columns based on visibility
  const columns = useMemo(() => {
    const visibleColumns: ColumnDef<Company>[] = []

    // Get visible columns from the view
    // columnVisibility keys are column IDs (with __), col.id is also column ID
    for (const col of allColumns) {
      const columnId = col.id
      const isVisible = columnVisibility[columnId] ?? col.isVisible
      if (isVisible) {
        visibleColumns.push(col)
      }
    }

    // Add any field definitions that are made visible but not in the view
    const viewColumnFields = new Set(allColumns.map((c) => c.fieldName))
    for (const [columnId, isVisible] of Object.entries(columnVisibility)) {
      // Convert column ID back to field name for lookup
      const fieldName = columnIdToFieldName(columnId)
      if (isVisible && !viewColumnFields.has(fieldName)) {
        const fieldDef = fieldDefinitionMap.get(fieldName)
        if (fieldDef) {
          const dataType = fieldDef.dataType as DataType
          const isNestedPath = fieldDef.fieldName.includes('.')
          visibleColumns.push({
            id: columnId,
            accessorFn: (row) =>
              isNestedPath
                ? getNestedValue(row, fieldDef.fieldName)
                : row[fieldDef.fieldName as keyof typeof row],
            header: fieldDef.displayName,
            fieldName: fieldDef.fieldName,
            displayName: fieldDef.displayName,
            dataType,
            isFilterable: fieldDef.isFilterable,
            isSortable: fieldDef.isSortable,
            isEditable: fieldDef.isEditable,
            options: fieldDef.options,
            width: 150,
            displayOrder: visibleColumns.length,
            isVisible: true,
            meta: {
              fieldName: fieldDef.fieldName,
              displayName: fieldDef.displayName,
              dataType,
              isFilterable: fieldDef.isFilterable,
              isSortable: fieldDef.isSortable,
              isEditable: fieldDef.isEditable,
              options: fieldDef.options,
              dataSource: fieldDef.dataSource,
              format: fieldDef.format,
              ...getBadgeConfig(fieldDef.fieldName),
            },
          })
        }
      }
    }

    return visibleColumns
  }, [allColumns, columnVisibility, fieldDefinitionMap])

  // Fields excluded from the column visibility picker (internal/technical fields)
  const EXCLUDED_FROM_COLUMN_PICKER = useMemo(
    () =>
      new Set([
        'id',
        'location',
        'location.country.id',
        'location.state.id',
        'location.city.id',
        'location.country.name',
        'location.state.name',
        'location.city.name',
        'createdAt',
        'updatedAt',
        'description',
      ]),
    []
  )

  // Build column visibility items from field definitions (excluding internal fields)
  // Uses column IDs (with __) to match columnOrder and header.id
  const columnVisibilityItems = useMemo((): ColumnVisibilityItem[] => {
    if (fieldDefinitions.length === 0) return []

    const pickerFields = fieldDefinitions.filter(
      (f) => !EXCLUDED_FROM_COLUMN_PICKER.has(f.fieldName)
    )

    // Create a map of view columns for quick lookup
    const viewColumnMap = new Map(
      (viewColumns ?? ([] as ViewColumn[])).map((col) => [col.fieldName, col])
    )

    // Build items from field definitions
    const items = pickerFields.map((field) => {
      const columnId = fieldNameToColumnId(field.fieldName)
      const viewColumn = viewColumnMap.get(field.fieldName)
      const isInView = !!viewColumn

      // Use columnVisibility (which includes overrides)
      // Keys in columnVisibility are column IDs (with __)
      let isVisible = false
      if (columnVisibility[columnId] !== undefined) {
        isVisible = columnVisibility[columnId]
      } else if (isInView && viewColumn) {
        isVisible = viewColumn.isVisible
      }

      return {
        id: columnId,
        label: field.displayName,
        isVisible,
      }
    })

    // Sort items based on columnOrder (which also uses column IDs)
    if (columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((id, index) => [id, index]))
      items.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER
        const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
    }

    return items
  }, [
    fieldDefinitions,
    viewColumns,
    columnVisibility,
    columnOrder,
    EXCLUDED_FROM_COLUMN_PICKER,
  ])

  // Handlers
  const handleSortChange = useCallback(
    (newSorting: SortingState) => {
      if (!viewId) return
      // Convert TanStack SortingState to Sort[] for store
      const newSorts = sortingStateToSortDto(newSorting)
      setWorkingSorts(viewId, newSorts)
    },
    [viewId, setWorkingSorts]
  )

  // Add a new sort column
  const handleSortSelect = useCallback(
    (sortValue: string) => {
      if (!viewId) return
      // Add new sort with ascending direction by default
      const newSorting: SortingState = [
        ...sorting,
        { id: sortValue, desc: false },
      ]
      const newSorts = sortingStateToSortDto(newSorting)
      setWorkingSorts(viewId, newSorts)
    },
    [viewId, sorting, setWorkingSorts]
  )

  // Update an existing sort (change column or direction)
  const handleSortUpdate = useCallback(
    (oldSort: string, newSortValue: SortingState) => {
      if (!viewId || newSortValue.length === 0) return
      const sortIndex = sorting.findIndex((s) => s.id === oldSort)
      if (sortIndex === -1) return

      const newSorting = [...sorting]
      newSorting[sortIndex] = newSortValue[0]
      const newSorts = sortingStateToSortDto(newSorting)
      setWorkingSorts(viewId, newSorts)
    },
    [viewId, sorting, setWorkingSorts]
  )

  // Reorder sorts via drag-and-drop
  const handleSortReorder = useCallback(
    (newSorting: SortingState) => {
      if (!viewId) return
      const newSorts = sortingStateToSortDto(newSorting)
      setWorkingSorts(viewId, newSorts)
    },
    [viewId, setWorkingSorts]
  )

  // Clear one or all sorts
  const handleClearSorts = useCallback(
    (sortId?: string) => {
      if (!viewId) return
      if (sortId) {
        // Clear specific sort
        const newSorting = sorting.filter((s) => s.id !== sortId)
        const newSorts = sortingStateToSortDto(newSorting)
        setWorkingSorts(viewId, newSorts)
      } else {
        // Clear all sorts
        setWorkingSorts(viewId, [])
      }
    },
    [viewId, sorting, setWorkingSorts]
  )

  const handleColumnOrderChange = useCallback(
    (newOrder: string[]) => {
      // Update local state immediately for responsive UI
      setColumnOrderOverride({ viewId, value: newOrder })

      // Mark column changes as pending (requires explicit save)
      if (viewId) {
        setHasPendingColumnChanges(viewId, true)
      }
    },
    [viewId, setHasPendingColumnChanges]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      if (!viewId) return
      setWorkingSearchTerm(viewId, value)
    },
    [viewId, setWorkingSearchTerm]
  )

  const handleFilterChange = useCallback(
    (filters: Filter[]) => {
      if (!viewId) return
      setWorkingFilters(viewId, filters)
    },
    [viewId, setWorkingFilters]
  )

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, isVisible: boolean) => {
      // Create new visibility state with the update
      const newVisibility = {
        ...columnVisibility,
        [columnId]: isVisible,
      }
      setColumnVisibilityOverride({ viewId, value: newVisibility })

      // Mark column changes as pending (requires explicit save)
      if (viewId) {
        setHasPendingColumnChanges(viewId, true)
      }
    },
    [viewId, columnVisibility, setHasPendingColumnChanges]
  )

  // Handle batch column visibility change (for Show All / Hide All)
  const handleBatchColumnVisibilityChange = useCallback(
    (changes: Record<string, boolean>) => {
      const newVisibility = { ...columnVisibility, ...changes }
      setColumnVisibilityOverride({ viewId, value: newVisibility })
      if (viewId) {
        setHasPendingColumnChanges(viewId, true)
      }
    },
    [viewId, columnVisibility, setHasPendingColumnChanges]
  )

  // Handle column sizing change
  const handleColumnSizingChange = useCallback(
    (columnId: string, width: number) => {
      // Ensure minimum width
      const MIN_WIDTH = 60
      const safeWidth = Math.max(width, MIN_WIDTH)

      // Update local state immediately
      const newSizing = {
        ...columnSizing,
        [columnId]: safeWidth,
      }
      setColumnSizingOverride({ viewId, value: newSizing })

      // Mark column changes as pending (requires explicit save)
      if (viewId) {
        setHasPendingColumnChanges(viewId, true)
      }
    },
    [viewId, columnSizing, setHasPendingColumnChanges]
  )

  // Get current columns state for explicit save
  const getCurrentColumns = useCallback(() => {
    return columnOrder
      .filter(
        (colId) =>
          viewColumnFieldNames.has(columnIdToFieldName(colId)) ||
          columnVisibility[colId] === true
      )
      .map((colId, index) => ({
        fieldName: columnIdToFieldName(colId),
        displayOrder: index,
        isVisible: columnVisibility[colId] ?? true,
        width: columnSizing[colId],
      }))
  }, [columnOrder, columnVisibility, columnSizing, viewColumnFieldNames])

  // While the search is gated on view + working-state hydration the
  // underlying query reports isLoading=false (it's disabled). Treat that
  // pending window as still loading so the UI doesn't briefly flicker
  // an empty state between view-load and the first fetch. Gate on
  // !isViewError so a failed view fetch surfaces the error state instead
  // of pinning isLoading=true forever.
  const isLoading =
    isLoadingView ||
    isLoadingFields ||
    isLoadingData ||
    (!!viewId && !isSearchReady && !isViewError)

  // Reset filters and sorts to base state
  const handleResetFiltersAndSorts = useCallback(() => {
    if (!viewId) return
    resetWorkingState(viewId)
  }, [viewId, resetWorkingState])

  // Revert all changes (filters, sorts, and columns)
  const handleRevertAllChanges = useCallback(() => {
    if (!viewId) return
    // Reset store state (filters, sorts, and column flag)
    resetWorkingState(viewId)
    // Clear local column overrides
    setColumnOrderOverride({ viewId, value: null })
    setColumnVisibilityOverride({ viewId, value: null })
    setColumnSizingOverride({ viewId, value: null })
  }, [viewId, resetWorkingState])

  // Check for unsaved changes
  const hasUnsavedChanges = viewId ? checkHasUnsavedChanges(viewId) : false

  return {
    data,
    columns,
    isLoading,
    isLoadingMore,
    isError: isDataError,
    hasMore,
    fetchMore,
    sorting,
    onSortChange: handleSortChange,
    onSortSelect: handleSortSelect,
    onSortUpdate: handleSortUpdate,
    onSortReorder: handleSortReorder,
    clearSorts: handleClearSorts,
    columnOrder,
    onColumnOrderChange: handleColumnOrderChange,
    columnSizing,
    onColumnSizingChange: handleColumnSizingChange,
    columnVisibility,
    columnVisibilityItems,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onBatchColumnVisibilityChange: handleBatchColumnVisibilityChange,
    searchTerm,
    onSearchChange: handleSearchChange,
    filters: workingFilters,
    onFilterChange: handleFilterChange,
    hasUnsavedChanges,
    resetFiltersAndSorts: handleResetFiltersAndSorts,
    revertAllChanges: handleRevertAllChanges,
    getCurrentColumns,
    refetch,
  }
}
